/**
 * ui/AdminShell — coquille d'administration pilotée par `AdminSchema`.
 * @author Dr Hamid MADANI <drmdh@msn.com>
 *
 * UI — futur `@mostajs/app-admin-ui-react` (N2). Charge la config (lecture
 * publique), édite section par section (SchemaForm), ÉCRIT via `PUT /api/config`
 * AUTHENTIFIÉE (D1) — via le MOTEUR. Tout vient du `schema` injecté. i18n
 * AR/FR/EN + RTL (D12). Sections filtrées par rôle (germe D11 → @mostajs/rbac).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthProvider';
import { makeT, isRtl, LOCALES } from '../engine/i18n';
import { setPath } from '../engine/paths';
import SchemaForm from './SchemaForm';
import SeedIO from './SeedIO';
import SeedStore from './SeedStore';
import LicenseManager from './LicenseManager';
import BackupManager from './BackupManager';
import PublishUpdate from './PublishUpdate';
import DisplaysApproval from '../ticketflow/DisplaysApproval';
import DisplaysLocal from './DisplaysLocal';

export default function AdminShell({ schema, centralSiteId = '', centralSiteName = '' }) {
  const { api, user, role, logout } = useAuth();
  const [locale, setLocale] = useState('fr');
  const [data, setData] = useState(null);
  const [activeId, setActiveId] = useState(schema.sections[0]?.id);
  const [view, setView] = useState('config'); // 'config' | 'store'
  const [dirty, setDirty] = useState(false);
  const [status, setStatus] = useState('');
  const t = useMemo(() => makeT(locale), [locale]);
  // Le magasin de seeds écrit la config → réservé aux rôles porteurs de config.write.
  const canWrite = role === 'admin' || role === 'manager';
  const isAdmin = role === 'admin'; // gestion de licence = admin uniquement

  const sections = useMemo(
    () => schema.sections.filter((s) => !s.roles || s.roles.includes(role)),
    [schema, role],
  );

  useEffect(() => {
    api.getConfig().then(setData).catch((e) => setStatus(String(e?.message || e)));
  }, [api]);

  const onChange = (path, value) => {
    setData((d) => setPath(d, path, value));
    setDirty(true);
  };

  const save = async () => {
    setStatus('…');
    try {
      const saved = await api.saveConfig(data);
      setData(saved);
      setDirty(false);
      setStatus(t('config.saved'));
    } catch (e) {
      setStatus(e?.response?.status === 403 ? 'Accès refusé (rôle)' : String(e?.message || e));
    }
  };

  /**
   * Recharge la config depuis le serveur (après une action serveur, ex. upload pub,
   * « Vider la file d'attente », restauration d'une version).
   *
   * Ce rechargement ÉCRASE l'état édité. Déclenché par un bouton d'action de la même page,
   * il faisait donc disparaître en silence une saisie en cours — et repassait « Enregistrer »
   * en grisé, donnant l'impression que le bouton ne s'activait plus. On demande désormais
   * confirmation avant d'abandonner des modifications non enregistrées.
   */
  const reload = () => {
    if (dirty && !window.confirm(t('config.discard'))) return Promise.resolve();
    return api.getConfig().then((d) => { setData(d); setDirty(false); }).catch(() => {});
  };

  const active = sections.find((s) => s.id === activeId) || sections[0];

  return (
    <div className="admin-shell" dir={isRtl(locale) ? 'rtl' : 'ltr'}>
      <header className="topbar">
        <strong>{t.localized(schema.title, 'TicketFlow-admin')}</strong>
        <span className="spacer" />
        <select value={locale} onChange={(e) => setLocale(e.target.value)}>
          {LOCALES.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
        </select>
        <span className="user">{user?.fullName || user?.username} · {role}</span>
        <SeedIO api={api} reload={reload} />
        <button onClick={save} disabled={!dirty}>{t('config.save')}</button>
        <button className="ghost" onClick={logout}>⎋</button>
      </header>
      {status && <div className="status">{status}</div>}
      <div className="body">
        <nav className="sections">
          {(canWrite || isAdmin) && (
            <div className="nav-tools">
              {canWrite && (
                <button
                  className={view === 'store' ? 'active' : ''}
                  onClick={() => setView('store')}
                >
                  🏪 Magasin de seeds
                </button>
              )}
              {isAdmin && (
                <button
                  className={view === 'license' ? 'active' : ''}
                  onClick={() => setView('license')}
                >
                  🔑 Licence
                </button>
              )}
              {isAdmin && (
                <button
                  className={view === 'update' ? 'active' : ''}
                  onClick={() => setView('update')}
                >
                  🔄 Mise à jour
                </button>
              )}
              {/* Sauvegardes : `canWrite` (admin/manager), comme le magasin de seeds. Un manager
                  doit pouvoir sauvegarder et TESTER la restauration de son propre site — c'est
                  une responsabilité d'exploitation, pas un privilège d'éditeur. */}
              {canWrite && (
                <button
                  className={view === 'backup' ? 'active' : ''}
                  onClick={() => setView('backup')}
                >
                  💾 Sauvegardes
                </button>
              )}
            </div>
          )}
          {/* Onglet « Écrans ». Deux contenus pour un même besoin, selon d'où l'on administre :
              — console centrale : APPROBATION signée des affichages facturés (DisplaysApproval) ;
              — admin.exe en LAN : réglages locaux (nom, son, test, oubli) + état du quota de
                licence, SANS approbation — le serveur refuse `enabled` hors ordre signé.
              L'onglet était auparavant conditionné à `centralSiteId`, donc absent d'admin.exe :
              c'est-à-dire absent exactement là où l'on en a besoin, sur site, quand le central
              est injoignable et qu'un écran reste noir sans qu'on sache pourquoi. */}
          {(centralSiteId || canWrite) && (
            <div className="nav-tools">
              <button
                className={view === 'displays' ? 'active' : ''}
                onClick={() => setView('displays')}
              >
                📺 Écrans
              </button>
            </div>
          )}
          {sections.map((s) => (
            <button
              key={s.id}
              className={view === 'config' && s.id === activeId ? 'active' : ''}
              onClick={() => { setView('config'); setActiveId(s.id); }}
            >
              {t.localized(s.label, s.id)}
            </button>
          ))}
        </nav>
        <main>
          {view === 'store' && <SeedStore api={api} reload={reload} />}
          {view === 'license' && <LicenseManager api={api} />}
          {view === 'update' && <PublishUpdate api={api} />}
          {view === 'backup' && <BackupManager api={api} />}
          {view === 'displays' && (centralSiteId
            ? <DisplaysApproval siteId={centralSiteId} siteName={centralSiteName} />
            : <DisplaysLocal api={api} role={role} />)}
          {view === 'config' && !data && <p>…</p>}
          {view === 'config' && data && active && (
            <SchemaForm section={active} data={data} onChange={onChange} api={api} reload={reload} t={t} />
          )}
        </main>
      </div>
    </div>
  );
}
